import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useState } from "react";
import api from "../services/api";
import GerarSenhaButton from "./GerarSenhaButton";

const PASSWORD_RESET_TOKEN_LENGTH = 6;
const PASSWORD_RULES = [
  {
    key: "minLength",
    label: "8+ caracteres",
    test: (password) => password.length >= 8,
  },
  {
    key: "hasUppercase",
    label: "1 letra maiuscula",
    test: (password) => /[A-Z]/.test(password),
  },
  {
    key: "hasLowercase",
    label: "1 letra minuscula",
    test: (password) => /[a-z]/.test(password),
  },
  {
    key: "hasNumber",
    label: "1 numero",
    test: (password) => /[0-9]/.test(password),
  },
  {
    key: "hasSpecialChar",
    label: "1 caractere especial",
    test: (password) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password),
  },
];

function EsqueciSenhaModal({ open, onClose }) {
  const [activeStep, setActiveStep] = useState(0);
  const [email, setEmail] = useState("");
  const [token, setToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const steps = [
    "Verificar Email",
    "Verificar Token",
    "Nova Senha",
    "Confirmação",
  ];

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const resetForm = () => {
    setActiveStep(0);
    setEmail("");
    setToken("");
    setNewPassword("");
    setConfirmPassword("");
    setError("");
    setSuccess("");
    setLoading(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
  };

  const handleNextStep1 = async () => {
    setError("");
    setSuccess("");

    if (!email) {
      setError("Por favor, insira seu email");
      return;
    }

    if (!isValidEmail(email)) {
      setError("Por favor, insira um email válido");
      return;
    }

    setLoading(true);

    try {
      const response = await api.post("/RedefinicaoDeSenha/RedefinirSenha", {
        emailsolicitante: email,
      });

      if (response.status === 200) {
        setSuccess(
          response.data?.message ||
            response.data?.Message ||
            "Se o e-mail estiver cadastrado, você receberá instruções.",
        );
        setTimeout(() => {
          setActiveStep(1);
          setSuccess("");
        }, 2000);
      }
    } catch (err) {
      console.error("Erro ao solicitar redefinição de senha:", err);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Erro ao processar solicitação",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleNextStep2 = async () => {
    setError("");
    setSuccess("");

    if (!token) {
      setError("Por favor, insira o token");
      return;
    }

    if (!isValidToken(token)) {
      setError(
        `Token inválido. Use exatamente ${PASSWORD_RESET_TOKEN_LENGTH} caracteres alfanuméricos, respeitando maiúsculas e minúsculas.`,
      );
      return;
    }

    setLoading(true);

    try {
      const response = await api.post("/RedefinicaoDeSenha/validar-token", {
        EmailSolicitante: email,
        token: token,
      });

      if (response.status === 200) {
        setSuccess("Token validado com sucesso!");
        setTimeout(() => {
          setActiveStep(2);
          setSuccess("");
        }, 2000);
      }
    } catch (err) {
      console.error("Erro ao validar token:", err);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Token inválido ou expirado",
      );
    } finally {
      setLoading(false);
    }
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isValidToken = (token) => {
    const tokenRegex = new RegExp(
      `^[a-zA-Z0-9]{${PASSWORD_RESET_TOKEN_LENGTH}}$`,
    );
    return tokenRegex.test(token);
  };

  const validatePasswordStrength = (password) => {
    if (!password) {
      return {
        hasErrors: true,
        isRequiredError: true,
        missingRules: PASSWORD_RULES.map((rule) => rule.label),
      };
    }

    const missingRules = PASSWORD_RULES.filter(
      (rule) => !rule.test(password),
    ).map((rule) => rule.label);

    return {
      hasErrors: missingRules.length > 0,
      isRequiredError: false,
      missingRules,
    };
  };

  const formatPasswordValidationMessage = (passwordValidation) => {
    if (passwordValidation.isRequiredError) {
      return "Informe a nova senha para continuar.";
    }

    return `Senha muito fraca. Adicione: ${passwordValidation.missingRules.join(", ")}.`;
  };

  const getPasswordStatus = (password) => {
    const rulesStatus = PASSWORD_RULES.map((rule) => ({
      key: rule.key,
      label: rule.label,
      ok: !!password && rule.test(password),
    }));

    const satisfiedCount = rulesStatus.filter((rule) => rule.ok).length;
    const progress = Math.round((satisfiedCount / PASSWORD_RULES.length) * 100);
    const missingLabels = rulesStatus
      .filter((rule) => !rule.ok)
      .map((rule) => rule.label);

    return {
      rulesStatus,
      satisfiedCount,
      progress,
      missingLabels,
    };
  };

  const handleNextStep3 = async () => {
    setError("");
    setSuccess("");

    // Validar força da senha
    const passwordValidation = validatePasswordStrength(newPassword);
    if (passwordValidation.hasErrors) {
      setError(formatPasswordValidationMessage(passwordValidation));
      return;
    }

    if (!confirmPassword) {
      setError("Por favor, confirme sua senha");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("As senhas não coincidem.");
      return;
    }

    setLoading(true);

    try {
      const response = await api.post("/RedefinicaoDeSenha/alterar-senha", {
        EmailSolicitante: email,
        Token: token,
        Senha: newPassword,
        ConfirmarSenha: confirmPassword,
      });

      if (response.status === 200) {
        setSuccess("Senha alterada com sucesso!");
        setTimeout(() => {
          setActiveStep(3);
          setSuccess("");
        }, 2000);
      }
    } catch (err) {
      console.error("Erro ao alterar senha:", err);
      setError(
        err.response?.data?.message || err.message || "Erro ao alterar senha",
      );
    } finally {
      setLoading(false);
    }
  };

  const passwordStatus = getPasswordStatus(newPassword);
  const allPasswordRequirementsMet =
    passwordStatus.satisfiedCount === PASSWORD_RULES.length;
  const isConfirmPasswordValid =
    confirmPassword.length > 0 && newPassword === confirmPassword;
  const canSubmitPasswordChange =
    !loading && allPasswordRequirementsMet && isConfirmPasswordValid;
  const passwordRequirementsText =
    passwordStatus.missingLabels.length > 0
      ? passwordStatus.missingLabels.join(", ")
      : "Todos os requisitos de senha foram atendidos.";

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          width: { xs: "calc(100% - 32px)", sm: 700 },
          maxWidth: "calc(100% - 32px)",
        },
      }}
    >
      <DialogTitle>Recuperar Senha</DialogTitle>
      <DialogContent>
        <Box sx={{ py: 2 }}>
          {/* Stepper completo no desktop */}
          <Box sx={{ display: { xs: "none", sm: "block" }, mb: 3 }}>
            <Stepper activeStep={activeStep}>
              {steps.map((label, index) => (
                <Step key={label} completed={activeStep > index}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>

          {/* Indicador compacto no mobile */}
          <Box
            sx={{
              display: { xs: "flex", sm: "none" },
              alignItems: "center",
              gap: 1,
              mb: 3,
            }}
          >
            {steps.map((label, index) => (
              <Box
                key={label}
                sx={{
                  flex: index === activeStep ? "0 0 auto" : "1 1 0",
                  height: 6,
                  borderRadius: 3,
                  bgcolor:
                    index < activeStep
                      ? "success.main"
                      : index === activeStep
                        ? "primary.main"
                        : "grey.300",
                  transition: "all 0.3s",
                }}
              />
            ))}
            <Typography
              variant="caption"
              sx={{ whiteSpace: "nowrap", color: "text.secondary", ml: 1 }}
            >
              {activeStep + 1} / {steps.length} — {steps[activeStep]}
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {/* Etapa 1: Solicitar Email */}
          {activeStep === 0 && (
            <Box>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Digite seu email para receber um token de recuperação.
              </Typography>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seu.email@exemplo.com"
                margin="normal"
              />
              <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
                <Button variant="outlined" onClick={handleClose} fullWidth>
                  Cancelar
                </Button>
                <Button
                  variant="contained"
                  onClick={handleNextStep1}
                  disabled={loading}
                  fullWidth
                >
                  {loading ? "Enviando..." : "Próximo"}
                </Button>
              </Box>
            </Box>
          )}

          {/* Etapa 2: Inserir Token */}
          {activeStep === 1 && (
            <Box>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Digite o token exatamente como foi enviado para seu email (diferencia maiúsculas e minúsculas).
              </Typography>
              <TextField
                fullWidth
                label="Token"
                value={token}
                onChange={(e) =>
                  setToken(
                    e.target.value.slice(0, PASSWORD_RESET_TOKEN_LENGTH),
                  )
                }
                margin="normal"
                inputProps={{ maxLength: PASSWORD_RESET_TOKEN_LENGTH }}
                helperText={`O token deve ter ${PASSWORD_RESET_TOKEN_LENGTH} caracteres.`}
              />
              <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setActiveStep(0);
                    setToken("");
                  }}
                  fullWidth
                >
                  Voltar
                </Button>
                <Button
                  variant="contained"
                  onClick={handleNextStep2}
                  disabled={loading}
                  fullWidth
                >
                  {loading ? "Verificando..." : "Próximo"}
                </Button>
              </Box>
            </Box>
          )}

          {/* Etapa 3: Inserir Nova Senha */}
          {activeStep === 2 && (
            <Box>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Digite sua nova senha e confirme-a.
              </Typography>
              <TextField
                fullWidth
                label="Nova Senha"
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                margin="normal"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        edge="end"
                        aria-label={
                          showNewPassword ? "Ocultar senha" : "Mostrar senha"
                        }
                      >
                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                fullWidth
                label="Confirmar Senha"
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                margin="normal"
                error={confirmPassword.length > 0 && !isConfirmPasswordValid}
                helperText={
                  confirmPassword.length > 0 && !isConfirmPasswordValid
                    ? "As senhas nao coincidem."
                    : ""
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        edge="end"
                        aria-label={
                          showConfirmPassword
                            ? "Ocultar confirmação da senha"
                            : "Mostrar confirmação da senha"
                        }
                      >
                        {showConfirmPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Box sx={{ mt: 1, mb: 1.5 }}>
                <LinearProgress
                  variant="determinate"
                  value={passwordStatus.progress}
                  sx={{ height: 8, borderRadius: 6 }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    mt: 1,
                    color:
                      passwordStatus.missingLabels.length > 0
                        ? "text.secondary"
                        : "success.main",
                  }}
                >
                  {passwordRequirementsText}
                </Typography>
              </Box>
              <Box
                sx={{
                  mt: 3,
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                <GerarSenhaButton
                  disabled={loading}
                  fullWidth
                  onGenerate={(senha) => {
                    setNewPassword(senha);
                    setConfirmPassword(senha);
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleNextStep3}
                  disabled={!canSubmitPasswordChange}
                  fullWidth
                >
                  {loading ? "Alterando..." : "Alterar Senha"}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setActiveStep(1);
                    setNewPassword("");
                    setConfirmPassword("");
                  }}
                  fullWidth
                >
                  Voltar
                </Button>
              </Box>
            </Box>
          )}

          {/* Etapa 4: Confirmação */}
          {activeStep === 3 && (
            <Box>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Sua senha foi alterada com sucesso!
              </Typography>
              <Button
                variant="contained"
                onClick={handleClose}
                fullWidth
                sx={{ mt: 2 }}
              >
                Voltar ao Login
              </Button>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default EsqueciSenhaModal;
